import React, { useMemo } from "react";
import { Star, Coffee, Wifi, RotateCcw } from "lucide-react";
import { Slider } from "@/components/ui/slider";
import { money } from "@/lib/api";

export const DEFAULT_FILTERS = { price: null, stars: 0, breakfast: false, wifi: false };

export function applyFilters(results, f) {
  return results.filter((h) =>
    (!f.price || (h.price_per_night >= f.price[0] && h.price_per_night <= f.price[1])) &&
    (!f.stars || h.star_rating >= f.stars) &&
    (!f.breakfast || h.perks.includes("Breakfast included")) &&
    (!f.wifi || h.perks.includes("Free Wi-Fi")));
}

const chip = (active) => `inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-xs font-medium transition-all duration-200 ${active ? "bg-accent text-accent-foreground shadow-sm" : "bg-secondary/60 text-muted-foreground hover:bg-secondary hover:text-foreground"}`;

export function HotelFilters({ results, filters, onChange }) {
  const [min, max] = useMemo(() => {
    const p = results.map((h) => h.price_per_night).filter((x) => x > 0);
    return p.length ? [Math.floor(Math.min(...p)), Math.ceil(Math.max(...p))] : [0, 0];
  }, [results]);
  const price = filters.price || [min, max];
  const dirty = filters.price || filters.stars || filters.breakfast || filters.wifi;

  return (
    <div className="mb-5 flex flex-col gap-4 rounded-2xl bg-card/70 p-4 ring-1 ring-border/50 lg:flex-row lg:items-center" data-testid="hotel-filters">
      <div className="flex-1 min-w-[220px]">
        <div className="mb-2 flex items-center justify-between text-xs">
          <span className="font-semibold uppercase tracking-wider text-muted-foreground">Budget / night</span>
          <span className="font-medium" data-testid="hotel-filter-price-label">{money(price[0])} – {money(price[1])}</span>
        </div>
        <Slider min={min} max={max || 1} step={1} value={price} onValueChange={(v) => onChange({ ...filters, price: v[0] === min && v[1] === max ? null : v })} disabled={max <= min} data-testid="hotel-filter-price" />
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {[[0, "Any stars"], [3, "3+"], [4, "4+"], [5, "5"]].map(([v, l]) => (
          <button key={v} type="button" onClick={() => onChange({ ...filters, stars: v })} className={chip(filters.stars === v)} data-testid={`hotel-filter-stars-${v}`}>{v > 0 && <Star className="h-3 w-3 fill-current" />}{l}</button>
        ))}
        <span className="mx-1 hidden h-5 w-px bg-border lg:block" />
        <button type="button" onClick={() => onChange({ ...filters, breakfast: !filters.breakfast })} className={chip(filters.breakfast)} data-testid="hotel-filter-breakfast"><Coffee className="h-3 w-3" /> Breakfast</button>
        <button type="button" onClick={() => onChange({ ...filters, wifi: !filters.wifi })} className={chip(filters.wifi)} data-testid="hotel-filter-wifi"><Wifi className="h-3 w-3" /> Free Wi-Fi</button>
        {dirty && <button type="button" onClick={() => onChange(DEFAULT_FILTERS)} className="inline-flex items-center gap-1 text-xs text-muted-foreground transition-colors hover:text-foreground" data-testid="hotel-filter-reset"><RotateCcw className="h-3 w-3" /> Reset</button>}
      </div>
    </div>
  );
}
